import { ResponsiveTable } from '@/components/shared/ResponsiveTable';
import { MovableDialog } from '@/components/shared/MovableDialog';
import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import {
  v2,
  type Role,
  type UserSummary,
  type UserInvitation,
} from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { parseApiErrorBody } from '@/api/v2Client';
import { USERS_ERROR_MESSAGES, INVITES_DISABLED_TEXT } from './usersErrorMessages';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { useUiToast } from '@/hooks/useUiToast';
import { Spinner } from '@/components/dashboard/Spinner';
import { Pagination } from '@/components/dashboard/Pagination';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { PageToast } from '@/components/shared/PageToast';
import { formatDate } from '@/lib/format';
import { PermissionDenied } from '@/pages/PermissionDenied';

// Backend (src/routes/users.ts) emits `{ ok: false, error, message? }`
// for 4xx/5xx. Codes that ship with a `message` (ValidationError,
// RoleNotFound, LastAdminProtection) fall through to `parsed.message`
// for per-field detail. Bare codes (NotFound, AuthRequired,
// PermissionDenied) get a friendlier rewrite via the map below. Phase
// E-polish-extract hoisted the formatXxxError helper to `@/lib/errors`;
// this map is passed in as the `codeMap` argument.

const PAGE_SIZE = 25;

interface ModalState {
  readonly user: UserSummary;
}

interface DeactivateTargetState {
  readonly user: UserSummary;
  readonly action: 'deactivate' | 'reactivate';
}

const tdStyle: CSSProperties = { padding: '12px 8px', verticalAlign: 'top' };

// ---- Styles for deactivated badges -----

const deactivatedBadgeStyle: CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 700,
  background: '#f3f4f6',
  color: '#6b7280',
  marginLeft: 8,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

// ---- Invite Modal -------------------------------------------------------

interface InviteModalProps {
  readonly roles: Role[];
  readonly onClose: () => void;
  readonly onInvited: (inviteLink: string) => void;
}

function InviteModal({ roles, onClose, onInvited }: InviteModalProps): ReactElement {
  const [email, setEmail] = useState('');
  // D-26 (T-06) — NOTHING is preselected. This used to default to the first
  // non-retired role, which is 'admin' in the seeded order, so an admin who
  // tabbed past the Role field granted full administrative access by
  // accident. An invitation's role must be a deliberate choice, and the
  // dialog unmounts on close, so reopening starts blank again.
  const [roleId, setRoleId] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (): Promise<void> => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Email is required.');
      return;
    }
    if (!roleId) {
      setError('Please select a role.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await v2.users.invite(trimmedEmail, roleId, message.trim() || undefined);
      onInvited(result.inviteLink);
      onClose();
    } catch (err) {
      setError(formatApiError(err, 'Failed to send invite', USERS_ERROR_MESSAGES));
    } finally {
      setSubmitting(false);
    }
  };

  // phase-movable-dialogs — shared movable shell.
  return (
    <MovableDialog
      persistKey="users.invite"
      title="Invite user"
      ariaLabel="Invite user"
      testId="invite-modal"
      onClose={onClose}
      closeOnBackdrop
      width="min(500px, 92vw)"
      zIndex={300}
      headerRight={
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}
        >
          ×
        </button>
      }
    >
      <>


        {error && (
          <div
            role="alert"
            style={{ color: 'var(--red)', background: 'var(--red-bg)', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 13 }}
          >
            {error}
          </div>
        )}

        <label style={{ display: 'block', marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Email address *</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@company.com"
            data-testid="invite-email-input"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Role *</span>
          <select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            data-testid="invite-role-select"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 }}
          >
            {/* D-26 — the empty option is the initial state; it is not a
                selectable role, and Send invite stays disabled while it is
                chosen. */}
            <option value="">— Select a role —</option>
            {/* phase-fix-role-retire-not-delete — retired roles cannot be assigned. */}
            {roles.filter((r) => !r.IsRetired).map((r) => (
              <option key={r.Id} value={r.Id}>{r.Name}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'block', marginBottom: 20 }}>
          <span style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Personal message (optional)</span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="I'm inviting you to manage eCTD submissions on AutoeCTD."
            data-testid="invite-message-input"
            rows={3}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
          />
        </label>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose}
            style={{ padding: '8px 18px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            // D-26 — no role chosen means nothing to send.
            disabled={submitting || !roleId}
            data-testid="invite-submit-btn"
            style={{ padding: '8px 18px', background: 'var(--primary, #4f46e5)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: submitting ? 'wait' : !roleId ? 'not-allowed' : 'pointer', fontSize: 14, opacity: submitting || !roleId ? 0.6 : 1 }}
          >
            {submitting ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </>
    </MovableDialog>
  );
}

// ---- Pending Invitations Panel ------------------------------------------

interface PendingInvitationsPanelProps {
  readonly invitations: UserInvitation[];
  readonly canManage: boolean;
  /**
   * R05 — CANCEL stays available while invitations are switched off (an
   * administrator must be able to clear the ones already out); only RESEND
   * is disabled. The two buttons sit side by side, so the distinction is
   * deliberate rather than an oversight.
   */
  readonly invitesEnabled: boolean;
  readonly onCancel: (id: string) => void;
  readonly onResend: (id: string) => void;
}

function PendingInvitationsPanel({ invitations, canManage, invitesEnabled, onCancel, onResend }: PendingInvitationsPanelProps): ReactElement {
  if (invitations.length === 0) return <></>;
  return (
    <div
      data-testid="pending-invitations-panel"
      style={{
        marginBottom: 24,
        border: '1px solid var(--border)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '10px 14px', background: 'var(--surface2, #f9fafb)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>
        Pending invitations ({invitations.length})
      </div>
      {invitations.map((inv) => (
        <div
          key={inv.id}
          data-testid={`invitation-row-${inv.email}`}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}
        >
          <span style={{ flex: 1, fontSize: 14 }}>{inv.email}</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inv.role.Name}</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Expires {formatDate(inv.expiresAt)}</span>
          {canManage && (
            <>
              <button
                type="button"
                data-testid={`invitation-resend-${inv.id}`}
                onClick={() => onResend(inv.id)}
                // R05 — the server refuses a resend while invitations are off,
                // so the button says so instead of offering a 403.
                disabled={!invitesEnabled}
                title={invitesEnabled ? undefined : INVITES_DISABLED_TEXT}
                style={{ fontSize: 12, padding: '3px 10px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 4, cursor: invitesEnabled ? 'pointer' : 'not-allowed', color: 'var(--text)', opacity: invitesEnabled ? 1 : 0.5 }}
              >
                Resend
              </button>
              <button
                type="button"
                data-testid={`invitation-cancel-${inv.id}`}
                onClick={() => onCancel(inv.id)}
                style={{ fontSize: 12, padding: '3px 10px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--red, #b91c1c)' }}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

// ---- Main Page ----------------------------------------------------------

export function UsersPage(): ReactElement {
  const perms = useUserPermissions();
  const canRead = perms.hasPermission('Users', 'read');
  // T-07 (D-27) — each control follows the permission its route checks:
  // invitations Users.manage; Edit roles, Deactivate and Reactivate
  // Users.write; Remove Users.delete. Manage does not imply write or delete.
  const canManage = perms.hasPermission('Users', 'manage');
  const canWrite = perms.hasPermission('Users', 'write');
  const canDelete = perms.hasPermission('Users', 'delete');
  const hasRowActions = canWrite || canDelete;
  // R05 — the SAME switch the backend enforces, read from /users/me, so the
  // page never offers a control the server will refuse.
  const invitesEnabled = perms.invitesEnabled;

  const [users, setUsers] = useState<UserSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('All');
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserSummary | null>(null);
  const [selfRemovalTarget, setSelfRemovalTarget] = useState<UserSummary | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<DeactivateTargetState | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const { toast, showToast, clearToast } = useUiToast();

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = globalThis.setTimeout(() => setDebouncedSearch(search), 300);
    return () => globalThis.clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, roleFilter]);

  useEffect(() => {
    if (!canRead) return;
    v2.roles.list().then(setRoles).catch(() => setRoles([]));
  }, [canRead]);

  // Fetch invitations alongside users.
  useEffect(() => {
    if (!canRead) return;
    v2.users.listInvitations().then(setInvitations).catch(() => setInvitations([]));
  }, [canRead, reloadKey]);

  useEffect(() => {
    if (!canRead) return;
    setUsers(null);
    setError(null);
    const q: { page: number; pageSize: number; role?: string; search?: string } = {
      page,
      pageSize: PAGE_SIZE,
    };
    if (roleFilter !== 'All') q.role = roleFilter;
    if (debouncedSearch.trim()) q.search = debouncedSearch.trim();
    v2.users
      .list(q)
      .then((res) => {
        setUsers(res.users);
        setTotal(res.total);
      })
      .catch((err: unknown) => {
        setError(formatApiError(err, 'Failed to load users', USERS_ERROR_MESSAGES));
        setUsers([]);
      });
  }, [canRead, page, debouncedSearch, roleFilter, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const handleDeleteConfirmed = async (user: UserSummary, confirmSelfLockout = false): Promise<void> => {
    setDeleteTarget(null);
    setSelfRemovalTarget(null);
    try {
      if (confirmSelfLockout) await v2.users.deleteUser(user.Email, { confirmSelfLockout: true });
      else await v2.users.deleteUser(user.Email);
      reload();
      showToast({ type: 'success', text: `Permanently removed ${user.Email}.` });
      void perms.refresh();
    } catch (err) {
      const parsed = parseApiErrorBody(err) as { error?: string; requiresConfirm?: boolean } | null;
      if (!confirmSelfLockout && parsed?.error === 'SelfLockout' && parsed.requiresConfirm === true) {
        setSelfRemovalTarget(user);
        return;
      }
      showToast({
        type: 'error',
        text: formatApiError(err, 'Failed to remove user', USERS_ERROR_MESSAGES),
      });
    }
  };

  const handleDeactivateConfirmed = async (state: DeactivateTargetState): Promise<void> => {
    setDeactivateTarget(null);
    try {
      if (state.action === 'deactivate') {
        await v2.users.deactivate(state.user.Email);
        showToast({ type: 'success', text: `Deactivated ${state.user.Email}. They will be blocked on next sign-in.` });
      } else {
        await v2.users.reactivate(state.user.Email);
        showToast({ type: 'success', text: `Reactivated ${state.user.Email}.` });
      }
      reload();
    } catch (err) {
      showToast({
        type: 'error',
        text: formatApiError(err, `Failed to ${state.action} user`, USERS_ERROR_MESSAGES),
      });
    }
  };

  const handleCancelInvitation = async (id: string): Promise<void> => {
    try {
      await v2.users.cancelInvitation(id);
      reload();
      showToast({ type: 'success', text: 'Invitation cancelled.' });
    } catch (err) {
      showToast({ type: 'error', text: formatApiError(err, 'Failed to cancel invitation', USERS_ERROR_MESSAGES) });
    }
  };

  const handleResendInvitation = async (id: string): Promise<void> => {
    try {
      const res = await v2.users.resendInvitation(id);
      showToast({ type: 'success', text: `Invite link refreshed: ${res.inviteLink}` });
    } catch (err) {
      showToast({ type: 'error', text: formatApiError(err, 'Failed to resend invitation', USERS_ERROR_MESSAGES) });
    }
  };

  if (perms.loading) return <Spinner caption="Checking permissions…" />;
  if (!canRead) return <PermissionDenied />;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>User Management</h1>
          <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
            Manage user role assignments. Bootstrap admins are auto-assigned on first sign-in.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            data-testid="invite-user-btn"
            onClick={() => setShowInviteModal(true)}
            // R05 — invitations are switched off until acceptance ships, and
            // the server refuses; the control reflects that rather than
            // opening a dialog whose Send answers 403.
            disabled={!invitesEnabled}
            title={invitesEnabled ? undefined : INVITES_DISABLED_TEXT}
            style={{
              padding: '8px 16px',
              background: invitesEnabled ? 'var(--primary, #4f46e5)' : 'var(--surface2, #e5e7eb)',
              color: invitesEnabled ? '#fff' : 'var(--text-muted)',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 13,
              cursor: invitesEnabled ? 'pointer' : 'not-allowed',
              whiteSpace: 'nowrap',
            }}
          >
            + Invite user
          </button>
        )}
      </div>

      {/* Pending invitations */}
      <PendingInvitationsPanel
        invitations={invitations}
        canManage={canManage}
        invitesEnabled={invitesEnabled}
        onCancel={(id) => void handleCancelInvitation(id)}
        onResend={(id) => void handleResendInvitation(id)}
      />

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search users"
          style={{ flex: 1, minWidth: 220, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 }}
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          aria-label="Role filter"
          style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 }}
        >
          <option value="All">All roles</option>
          {(roles ?? []).map((r) => (
            <option key={r.Id} value={r.Name}>{r.Name}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Showing {users?.length ?? 0} of {total} user{total === 1 ? '' : 's'}
        </span>
      </div>

      {users === null && !error && <Spinner caption="Loading users…" />}
      {error && (
        <div role="alert" style={{ color: 'var(--red)', marginBottom: 12 }}>
          {error}
          <button type="button" onClick={reload} style={{ marginLeft: 12, padding: '4px 12px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      )}

      {users !== null && (
        <>
          {(() => {
            const hasActiveFilter = debouncedSearch.trim() !== '' || roleFilter !== 'All';
            const colSpan = hasRowActions ? 5 : 4;
            return (
              <ResponsiveTable data-testid="users-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
                    <th style={tdStyle}>Name</th>
                    <th style={tdStyle}>Email</th>
                    <th style={tdStyle}>Roles</th>
                    <th style={tdStyle}>Assigned</th>
                    {hasRowActions && <th style={tdStyle}></th>}
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr data-testid="users-empty-row">
                      <td
                        colSpan={colSpan}
                        style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}
                      >
                        {!hasActiveFilter ? (
                          <>
                            <div style={{ fontSize: 32, marginBottom: 8 }} aria-hidden="true">👥</div>
                            <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>No users yet</div>
                            <div>
                              Users appear here after they sign in for the first time. Bootstrap admins are auto-assigned on first sign-in.
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>No users match the current filters</div>
                            <div>Showing 0 of {total} user{total === 1 ? '' : 's'}. Clear the search box or reset the role filter to see all users.</div>
                          </>
                        )}
                      </td>
                    </tr>
                  ) : (
                    users.map((u) => (
                      <tr
                        key={u.Email}
                        style={{
                          borderBottom: '1px solid var(--border)',
                          opacity: u.IsDeactivated ? 0.6 : 1,
                        }}
                        data-testid={`user-row-${u.Email}`}
                      >
                        <td style={tdStyle} data-testid={`user-name-${u.Email}`}>
                          {u.Name ? (
                            u.Name
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {u.Email}
                          {u.IsDeactivated && (
                            <span
                              style={deactivatedBadgeStyle}
                              data-testid={`user-deactivated-badge-${u.Email}`}
                            >
                              Deactivated
                            </span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {u.Roles.length === 0 ? (
                            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>
                          ) : (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {u.Roles.map((r) => (
                                <span
                                  key={r.Id}
                                  className="badge"
                                  style={{ background: '#dbeafe', color: '#1e3a8a', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}
                                >
                                  {r.Name}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={{ ...tdStyle, fontSize: 13, color: 'var(--text-muted)' }}>
                          {formatDate(u.AssignedAt)}
                        </td>
                        {hasRowActions && (
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                            {canWrite && (
                            <button
                              type="button"
                              data-testid={`user-edit-roles-btn-${u.Email}`}
                              onClick={() => setModal({ user: u })}
                              style={{ background: 'none', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 4, padding: '4px 10px', marginRight: 8, cursor: 'pointer', fontSize: 12 }}
                            >
                              Edit roles
                            </button>
                            )}
                            {!canWrite ? null : u.IsDeactivated ? (
                              <button
                                type="button"
                                data-testid={`user-reactivate-btn-${u.Email}`}
                                onClick={() => setDeactivateTarget({ user: u, action: 'reactivate' })}
                                style={{ background: 'none', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 4, padding: '4px 10px', marginRight: 8, cursor: 'pointer', fontSize: 12 }}
                              >
                                Reactivate
                              </button>
                            ) : (
                              <button
                                type="button"
                                data-testid={`user-deactivate-btn-${u.Email}`}
                                onClick={() => setDeactivateTarget({ user: u, action: 'deactivate' })}
                                style={{ background: 'none', color: 'var(--orange, #d97706)', border: 'none', cursor: 'pointer', fontSize: 12, marginRight: 8 }}
                              >
                                Deactivate
                              </button>
                            )}
                            {canDelete && (
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(u)}
                              data-testid={`user-delete-btn-${u.Email}`}
                              style={{ background: 'none', color: 'var(--red)', border: 'none', cursor: 'pointer', fontSize: 12 }}
                            >
                              Remove
                            </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </ResponsiveTable>
            );
          })()}
          {users.length > 0 && (
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          )}
        </>
      )}

      {modal && roles && (
        <RoleEditModal
          /* phase-roles-and-users-fixes — the LIVE row from the refreshed
             list; `modal.user` is only the open-time snapshot and goes stale
             the moment a role changes. */
          user={users?.find((u) => u.Email === modal.user.Email) ?? modal.user}
          roles={roles}
          onClose={() => setModal(null)}
          onChanged={(roleName, assigned) => {
            reload();
            void perms.refresh();
            showToast({
              type: 'success',
              text: assigned
                ? `Assigned role "${roleName}" to ${modal.user.Email}.`
                : `Revoked role "${roleName}" from ${modal.user.Email}.`,
            });
          }}
        />
      )}

      {deleteTarget && (
        /* D-17 (T-06 row 13) — this is the irreversible one, and it has to
           look and behave unlike Deactivate: a red title as well as a red
           button, and a confirm that stays disabled until the admin types
           the address they are removing.

           The copy was VERIFIED against a running server on an isolated
           copy rather than inferred: DELETE /api/v1/users/:email removes the
           UserRoles and UserTenants rows for THIS tenant and nothing else,
           and the AuditLog rows about the person remain (the removal adds
           one). The old line "The user will be re-created on their next
           sign-in" was true of the MEMBERSHIP row — /users/me upserts it —
           but it read as though the removal undoes itself, when the roles do
           NOT come back: the probe measured 1 role before, 0 after signing
           in again. So the sentence now says both halves, and "cannot be
           undone" is scoped to what genuinely cannot be: the roles and the
           membership. */
        <ConfirmModal
          title="Remove user permanently"
          titleTone="danger"
          message={`Remove ${deleteTarget.Email} from this organisation? Their roles and membership here will be removed, and that cannot be undone. If they sign in again they rejoin with no roles, and an administrator must assign roles again. Their Microsoft account and the audit history are not affected. To block access without removing them, use Deactivate instead.`}
          typeToConfirm={{
            expected: deleteTarget.Email,
            label: `To confirm, type ${deleteTarget.Email}`,
          }}
          confirmLabel="Remove permanently"
          variant="destructive"
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void handleDeleteConfirmed(deleteTarget)}
        />
      )}

      {selfRemovalTarget && (
        <ConfirmModal
          title="Remove my own access?"
          titleTone="danger"
          message="This removes your own roles and membership in this organisation. You will lose access, and another administrator must restore your roles before you can manage users again."
          confirmLabel="Remove my own access anyway"
          variant="destructive"
          onCancel={() => setSelfRemovalTarget(null)}
          onConfirm={() => void handleDeleteConfirmed(selfRemovalTarget, true)}
        />
      )}

      {deactivateTarget && (
        <ConfirmModal
          title={deactivateTarget.action === 'deactivate' ? 'Deactivate user' : 'Reactivate user'}
          message={
            deactivateTarget.action === 'deactivate'
              ? `Deactivate ${deactivateTarget.user.Email}? They will be blocked at their next sign-in. Nothing is deleted: their roles and membership are kept, and you can reactivate them at any time.`
              : `Reactivate ${deactivateTarget.user.Email}? They will be able to sign in again, with the roles they already have.`
          }
          confirmLabel={deactivateTarget.action === 'deactivate' ? 'Deactivate' : 'Reactivate'}
          /* D-17 — NOT destructive. Deactivation deletes nothing and is
             reversible from this same screen; painting its button the same
             red as permanent removal is the confusion row 13 is about. */
          variant="default"
          onCancel={() => setDeactivateTarget(null)}
          onConfirm={() => void handleDeactivateConfirmed(deactivateTarget)}
        />
      )}

      {showInviteModal && roles && (
        <InviteModal
          roles={roles}
          onClose={() => setShowInviteModal(false)}
          onInvited={(link) => {
            reload();
            showToast({ type: 'success', text: `Invite link created: ${link}` });
          }}
        />
      )}

      <PageToast toast={toast} onDismiss={clearToast} />
    </section>
  );
}

// ---- RoleEditModal (unchanged) -------------------------------------------

interface RoleEditModalProps {
  readonly user: UserSummary;
  readonly roles: Role[];
  readonly onClose: () => void;
  readonly onChanged: (roleName: string, assigned: boolean) => void;
}

function RoleEditModal({ user, roles, onClose, onChanged }: RoleEditModalProps): ReactElement {
  // phase-roles-and-users-fixes — the assigned set MIRRORS the live user
  // record. It used to initialise once from a snapshot taken when the dialog
  // opened; any remount (or a refreshed user) then resurrected that stale
  // snapshot, so a just-assigned role rendered as unassigned and the next
  // click ASSIGNED it again instead of removing it — the one-way toggle
  // Sharon hit. Syncing on the incoming role ids makes the dialog show what
  // the server actually holds, regardless of remounts.
  const serverRoleIds = user.Roles.map((r) => r.Id).join(',');
  const [assigned, setAssigned] = useState(() => new Set(user.Roles.map((r) => r.Id)));
  useEffect(() => {
    setAssigned(new Set(user.Roles.map((r) => r.Id)));
    // Keyed by the id LIST, so an unrelated re-render does not clobber an
    // in-flight optimistic update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverRoleIds]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // D-14 (T-06 #10) — the backend refuses a self-revoke of a Users:manage
  // role with 409 SelfLockout + requiresConfirm. Hold the role so the dialog
  // can offer an explicit "remove anyway" that resends with the confirm flag.
  const [selfLockoutRole, setSelfLockoutRole] = useState<Role | null>(null);

  const toggle = async (role: Role, confirmSelfLockout = false): Promise<void> => {
    setBusyId(role.Id);
    setError(null);
    setSelfLockoutRole(null);
    const wasAssigned = assigned.has(role.Id);
    setAssigned((prev) => {
      const next = new Set(prev);
      if (wasAssigned) next.delete(role.Id);
      else next.add(role.Id);
      return next;
    });
    try {
      if (wasAssigned) {
        if (confirmSelfLockout) {
          await v2.users.revokeRole(user.Email, role.Id, { confirmSelfLockout: true });
        } else {
          await v2.users.revokeRole(user.Email, role.Id);
        }
      } else {
        await v2.users.assignRole(user.Email, role.Id);
      }
      onChanged(role.Name, !wasAssigned);
    } catch (err) {
      setAssigned((prev) => {
        const next = new Set(prev);
        if (wasAssigned) next.add(role.Id);
        else next.delete(role.Id);
        return next;
      });
      setError(formatApiError(err, 'Role change failed', USERS_ERROR_MESSAGES));
      const parsed = parseApiErrorBody(err) as { error?: string; requiresConfirm?: boolean } | null;
      if (parsed?.error === 'SelfLockout' && parsed.requiresConfirm === true) {
        setSelfLockoutRole(role);
      }
    } finally {
      setBusyId(null);
    }
  };

  // phase-movable-dialogs — shared movable shell.
  return (
    <MovableDialog
      persistKey="users.editRoles"
      title={<>Edit roles for {user.Email}</>}
      ariaLabel="Edit roles"
      onClose={onClose}
      closeOnBackdrop
      width="min(560px, 90vw)"
      maxHeight="80vh"
      zIndex={300}
      headerRight={
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}
        >
          ×
        </button>
      }
    >
      <>


        {error && (
          <div role="alert"
            style={{ color: 'var(--red)', background: 'var(--red-bg)', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 13 }}
          >
            {error}
            {selfLockoutRole && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  data-testid="self-lockout-confirm"
                  onClick={() => void toggle(selfLockoutRole, true)}
                  style={{ background: 'var(--red)', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  Remove my own access anyway
                </button>
              </div>
            )}
          </div>
        )}

        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0 }}>
          Click a role to toggle it. Changes apply immediately.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* phase-fix-role-retire-not-delete — retired roles are excluded
              from assignment. Existing assignments to a retired role stay
              in the DB (history) but grant nothing while retired. */}
          {roles.filter((r) => !r.IsRetired).map((r) => {
            const isAssigned = assigned.has(r.Id);
            const isBusy = busyId === r.Id;
            // T-07 (D-27) — the server's delegation answer for THIS caller:
            // you may only assign or revoke a role whose permissions you hold.
            const missing = r.CallerMissing ?? [];
            const refused = missing.length > 0;
            const refusedWhy = refused
              ? `You cannot ${isAssigned ? 'remove' : 'assign'} ${r.Name}: it grants ${missing.map((k) => k.replace('.', ':')).join(', ')}, which you do not hold.`
              : undefined;
            return (
              <div key={r.Id}>
              <button
                type="button"
                onClick={() => toggle(r)}
                disabled={isBusy || refused}
                title={refusedWhy}
                data-testid={`role-toggle-${r.Name}`}
                aria-pressed={isAssigned}
                aria-describedby={refused ? `role-refused-${r.Id}` : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '10px 14px',
                  background: isAssigned ? 'var(--accent)' : 'var(--surface2)',
                  color: isAssigned ? '#fff' : 'var(--text)',
                  border: '1px solid ' + (isAssigned ? 'var(--accent)' : 'var(--border)'),
                  borderRadius: 6,
                  cursor: refused ? 'not-allowed' : isBusy ? 'wait' : 'pointer',
                  fontSize: 14,
                  textAlign: 'left',
                  opacity: isBusy || refused ? 0.6 : 1,
                }}
              >
                <span>
                  <strong>{r.Name}</strong>
                  <span style={{ fontSize: 12, opacity: 0.8, marginLeft: 8 }}>{r.Description}</span>
                </span>
                <span style={{ fontSize: 14, marginLeft: 12 }}>
                  {isBusy ? '…' : isAssigned ? '✓' : '+'}
                </span>
              </button>
              {refused && (
                <div
                  id={`role-refused-${r.Id}`}
                  data-testid={`role-refused-${r.Name}`}
                  style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 2px 0' }}
                >
                  {refusedWhy}
                </div>
              )}
              </div>
            );
          })}
        </div>

        {/* phase-roles-and-users-fixes item 2 — a real way out. The dialog
            previously offered only a small ×; Escape already worked (it is on
            the shared MovableDialog shell) but was undiscoverable. Changes
            apply immediately as each role is clicked, so this is "Done", not
            "Save". */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            type="button"
            onClick={onClose}
            data-testid="role-edit-done"
            style={{
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '8px 18px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Done
          </button>
        </div>
      </>
    </MovableDialog>
  );
}
